import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertCircle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { formatAmount } from "../lib/booking-success";

const MAX_POLLS = 15;   // 15 × 2s = 30s total
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");   // Stripe flow
  const bookingId = params.get("booking_id");   // Rapyd flow (and Stripe)
  const [state, setState] = useState({ phase: "polling", attempts: 0 });

  // Decide provider flow: presence of session_id means Stripe return; otherwise Rapyd.
  const isRapydFlow = !sessionId && !!bookingId;

  useEffect(() => {
    if (!sessionId && !bookingId) {
      setState({ phase: "error", message: "Missing booking reference" });
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      attempt += 1;
      try {
        const url = isRapydFlow
          ? `/payments/rapyd/status/${bookingId}`
          : `/payments/status/${sessionId}`;
        const { data } = await api.get(url);
        if (cancelled) return;

        const isPaid = isRapydFlow
          ? data.paid === true
          : data.payment_status === "paid";
        if (isPaid) {
          setState({ phase: "paid", data });
          return;
        }
        const isExpired = isRapydFlow
          ? data.checkout_status === "expired" || data.payment_status === "expired" || data.status === "cancelled"
          : data.status === "expired";
        if (isExpired) {
          setState({ phase: "expired", data });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ phase: "timeout", data });
          return;
        }
        setState({ phase: "polling", attempts: attempt });
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attempt >= MAX_POLLS) {
          setState({ phase: "error", message: "Could not verify payment." });
          return;
        }
        // Transient error — keep polling
        setState({ phase: "polling", attempts: attempt });
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, bookingId, isRapydFlow]);

  return (
    <section className="mx-auto max-w-xl px-5 sm:px-8 py-24" data-testid="payment-success-page">
      {state.phase === "polling" && (
        <Card icon={<Clock className="text-slate-600 animate-pulse" size={22} />} tint="slate" testid="payment-polling">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Payment received, confirming…</h1>
          <p className="mt-2 text-sm text-slate-600">
            This usually takes just a few seconds. Attempt {state.attempts || 1}/{MAX_POLLS}.
          </p>
        </Card>
      )}
      {state.phase === "paid" && (
        <Card icon={<CheckCircle2 className="text-emerald-600" size={22} />} tint="emerald" testid="payment-paid">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Payment received — thank you!</h1>
          <p className="mt-2 text-sm text-slate-600">
            Your booking has been paid and confirmed.
            We’ll email you the confirmation shortly.
          </p>
          <p className="mt-3 text-xs leading-5 text-slate-500" data-testid="payment-success-legal-note">
            Payments and invoices are processed by FONDO ehf., operator of IceFleet.
          </p>
          {bookingId && <p className="mt-2 text-xs text-slate-400">Booking ref: {bookingId}</p>}
          <div className="mt-6 flex gap-3">
            <Link to="/" data-testid="payment-success-home-btn" className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"><ArrowLeft size={14}/> Home</Link>
          </div>
        </Card>
      )}
      {(state.phase === "expired" || state.phase === "timeout" || state.phase === "error") && (
        <Card icon={<AlertCircle className="text-amber-600" size={22} />} tint="amber" testid="payment-not-paid">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            {state.phase === "error" ? "Something went wrong" : state.phase === "expired" ? "Payment session expired" : "Still confirming your payment"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {state.phase === "timeout"
              ? "Your payment may still be processing at the provider. You can safely leave this page — we'll email you confirmation as soon as it clears. If you paid and don't hear from us within 15 minutes, please contact us and we'll manually reconcile."
              : "Your booking was not confirmed because payment did not complete. Please try again if you still want to secure this tour."}
          </p>
          <p className="mt-3 text-xs leading-5 text-slate-500" data-testid="payment-failure-legal-note">
            Payments and invoices are processed by FONDO ehf., operator of IceFleet.
          </p>
          {bookingId && <p className="mt-2 text-xs text-slate-400">Booking ref: {bookingId}</p>}
          <div className="mt-6 flex gap-3">
            <Link to="/" data-testid="payment-not-paid-home-btn" className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"><ArrowLeft size={14}/> Home</Link>
            <Link to="/contact" data-testid="payment-not-paid-contact-btn" className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Contact us</Link>
          </div>
        </Card>
      )}
    </section>
  );
}

function Card({ icon, tint, children, testid }) {
  const bg = {
    slate: "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
  }[tint] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${bg} p-6 sm:p-8`}>
      <div className="mb-4">{icon}</div>
      {children}
    </div>
  );
}
